import axios from '@/modules/axiosInterceptor';
import { apiUrlPrefix } from '@/modules/staticData';

export default {
  mixins: [apiUrlPrefix],
  methods: {
    getPlaylist(playlistObj, suppressNotifications) {
      const uid = playlistObj.id;
      const deferred = new Promise((resolve, reject) => {
        axios.get(`${apiUrlPrefix}${this.playlistType}/${uid}`)
          .then((r) => {
            resolve(r.data.playlist);
          })
          .catch((r) => {
            if (!suppressNotifications) {
              const errorMessage = this.handleFormErrors(r.response.data);
              this.$root.$emit('notification-add', {
                content: `Unable to fetch the playlist: ${errorMessage}`,
                variant: 'danger',
                ttl: 10000,
              });
              reject(errorMessage);
            } else {
              reject();
            }
          })
          .finally(() => { this.formLoading = false; });
      });
      return deferred;
    },
    deletePlaylist(playlistObj) {
      const uid = playlistObj.id;
      const deferred = new Promise((resolve, reject) => {
        this.$root.$emit('modal-show-confirmation', {
          title: playlistObj.name,
          body: 'Delete the playlist permanently?',
          cbConfirm: () => {
            this.formLoading = true;
            axios.delete(`${apiUrlPrefix}${this.playlistType}/${uid}`)
              .then(() => {
                this.$root.$emit('notification-add', { content: 'Playlist deleted.' });
                resolve();
              })
              .catch((r) => {
                const errorMessage = this.handleFormErrors(r.response.data);
                this.$root.$emit('notification-add', {
                  content: `Unable to delete the playlist: ${errorMessage}`,
                  variant: 'danger',
                  ttl: 10000,
                });
                reject(errorMessage);
              })
              .finally(() => { this.formLoading = false; });
          },
          confirmText: 'Delete',
          cancelText: 'Cancel',
        });
      });
      return deferred;
    },
    publishPlaylist(playlistObj, action) {
      const uid = playlistObj.id;
      const deferred = new Promise((resolve, reject) => {
        this.$root.$emit('modal-show-confirmation', {
          title: playlistObj.name,
          body: `Are you sure you want to ${action} the playlist?`,
          cbConfirm: () => {
            this.formLoading = true;
            axios.patch(`${apiUrlPrefix}${this.playlistType}/${uid}/${action}`)
              .then(() => {
                this.$root.$emit('notification-add', { content: `Playlist ${action}ed.` });
                resolve();
              })
              .catch((r) => {
                const errorMessage = this.handleFormErrors(r.response.data);
                this.$root.$emit('notification-add', {
                  content: `Unable to ${action} the playlist: ${errorMessage}`,
                  variant: 'danger',
                  ttl: 10000,
                });
                reject(errorMessage);
              })
              .finally(() => { this.formLoading = false; });
          },
          confirmText: 'Yes',
          cancelText: 'No',
        });
      });
      return deferred;
    },
    updatePlaylist(playlistObj) {
      this.formLoading = true;
      const deferred = new Promise((resolve, reject) => {
        axios.put(`${apiUrlPrefix}${this.playlistType}`, { ...playlistObj }).then((r) => {
          this.$root.$emit('notification-add', { content: 'Playlist updated.' });
          resolve(r.data.playlist);
        }).catch((r) => {
          const errorMessage = this.handleFormErrors(r.response.data);
          this.$root.$emit('notification-add', {
            content: `Unable to update the playlist: ${errorMessage}`,
            variant: 'danger',
            ttl: 10000,
          });
          reject(errorMessage);
        }).finally(() => { this.formLoading = false; });
      });
      return deferred;
    },
    createPlaylist(playlistObj) {
      this.formLoading = true;
      const deferred = new Promise((resolve, reject) => {
        axios.post(`${apiUrlPrefix}${this.playlistType}`, { ...playlistObj }).then((r) => {
          this.$root.$emit('notification-add', { content: 'Playlist created.' });
          if (this.playlistType === 'playlists') {
            this.$router.push({ name: 'showPlaylist', params: { id: r.data.playlist.id, playlistObj: r.data.playlist } });
          } else if (this.playlistType === 'loop-playlists') {
            this.$router.push({ name: 'showLoopPlaylist', params: { id: r.data.playlist.id, playlistObj: r.data.playlist } });
          } else if (this.playlistType === 'weekly-playlists') {
            this.$router.push({ name: 'showWeeklyPlaylist', params: { id: r.data.playlist.id, playlistObj: r.data.playlist } });
          }
          resolve(r.data.playlist);
        }).catch((r) => {
          const errorMessage = this.handleFormErrors(r.response.data);
          this.$root.$emit('notification-add', {
            content: `Unable to create the playlist: ${errorMessage}`,
            variant: 'danger',
            ttl: 10000,
          });
          reject(errorMessage);
        }).finally(() => { this.formLoading = false; });
      });
      return deferred;
    },
    /**
     * Creates an autonomous playlist polling mechanism
     * @returns {object} reference to the interval
     * @param {playlistObj}
     * @description
     * - 40x - playlist deleted or bad auth -- abort the polling
     * - 20x - check 'ready' and 'error' fields and abort polling if any is active
     */
    playlistPollInterval(playlistObj) {
      const intervalInstance = setInterval(() => {
        this.getPlaylist(playlistObj, true)
          .then((playlist) => {
            if (playlist.ready === 1) {
              // playlist is ready
              clearInterval(intervalInstance);
              this.$root.$emit('notification-add', {
                content: `Playlist ${playlist.name} processed.`,
                variant: 'success',
                ttl: 10000,
                nextRoute: { name: 'showPlaylist', params: { id: playlist.id } },
              });
              this.$root.$emit('playlist-updated', playlist);
            } else if (playlist.error && playlist.error.length > 0) {
              // playlist error
              clearInterval(intervalInstance);
              this.$root.$emit('notification-add', {
                content: `Playlist ${playlist.name} not created: ${playlist.error}`,
                variant: 'danger',
                ttl: 10000,
                nextRoute: { name: 'showPlaylist', params: { id: playlist.id } },
              });
              this.$root.$emit('playlist-updated', playlist);
            }
          })
          .catch(() => {
            clearInterval(intervalInstance);
          });
      }, 10000);
      return intervalInstance;
    },
    async duplicatePlaylist(id) {
      try {
        const playlist = await this.getPlaylist({ id });
        // copy to a new object
        const clonePlaylist = ps => ({
          ...ps,
          // eslint-disable-next-line camelcase
          slides: ps.slides.map(s => s.pivot).map(({ file_id, ends_at, starts_at }) => ({ file_id, ends_at, starts_at })),
        });
        const newPlaylist = { ...playlist, name: `Copy of ${playlist.name}` };
        // regular and looped
        if (newPlaylist.slides) newPlaylist.slides = clonePlaylist(newPlaylist).slides;
        // weekly
        if (newPlaylist.days) newPlaylist.days = newPlaylist.days.map(ps => clonePlaylist(ps));
        delete newPlaylist.created_at;
        delete newPlaylist.id;
        await this.createPlaylist(newPlaylist);
        this.$root.$emit('notification-add', { content: 'Playlist duplicated.' });
      } catch (e) {
        this.$root.$emit('notification-add', {
          content: `Unable to duplicate the playlist: ${e}`,
          variant: 'danger',
          ttl: 10000,
        });
        throw e;
      }
    },
    getPlaylistUsage(playlistId, suppressNotifications) {
      const deferred = new Promise((resolve, reject) => {
        this.formLoading = true;
        axios.get(`${apiUrlPrefix}${this.playlistType}/${playlistId}/used-in`)
          .then((r) => {
            resolve(r.data.data);
          })
          .catch(() => {
            if (!suppressNotifications) {
              this.$root.$emit('notification-add', {
                content: `Unable to fetch the playlist usage info.`,
                variant: 'danger',
                ttl: 10000,
              });
            }
            reject();
          })
          .finally(() => { this.formLoading = false; });
      });
      return deferred;
    },
    appendFileToLoopedPlaylist(fileId, playlistId) {
      const deferred = new Promise((resolve, reject) => {
        this.formLoading = true;
        axios.patch(`${apiUrlPrefix}loop-playlists/${playlistId}/slides/${fileId}/add`).then((r) => {
          this.$root.$emit('notification-add', { content: 'File added to the selected playlist.' });
          resolve(r.data.playlist);
        }).catch((r) => {
          this.$root.$emit('notification-add', {
            content: `Unable to update the playlist: ${r.response.data.error}`,
            variant: 'danger',
            ttl: 10000,
          });
          reject(r.response.data.error);
        }).finally(() => { this.formLoading = false; });
      });
      return deferred;
    },
  },
};
